import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [msg, setMsg] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setStatus("error"); setMsg("This confirmation link is invalid."); return; }
    verifyEmail(token)
      .then(() => { setStatus("ok"); setTimeout(() => navigate("/"), 1800); })
      .catch((ex) => { setStatus("error"); setMsg(formatApiError(ex.response?.data?.detail) || ex.message); });
  }, [token, verifyEmail, navigate]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8">
      <div className="w-full max-w-md text-center" data-testid="verify-email-page">
        <img src={LOGO_URL} alt="OnchainLyfe" className="h-8 mx-auto mb-10" />
        {status === "loading" && (
          <div data-testid="verify-loading">
            <Loader2 className="h-10 w-10 text-[#ccff00] mx-auto animate-spin" />
            <h1 className="font-display text-2xl font-bold mt-6">Confirming your email…</h1>
          </div>
        )}
        {status === "ok" && (
          <div data-testid="verify-success">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#ccff00]/10 border border-[#ccff00]/20 mb-6">
              <CheckCircle2 className="h-7 w-7 text-[#ccff00]" />
            </div>
            <h1 className="font-display text-3xl font-bold">Email confirmed</h1>
            <p className="text-zinc-400 text-sm mt-3">You're all set. Taking you in…</p>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-error">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/10 border border-red-500/20 mb-6">
              <XCircle className="h-7 w-7 text-red-400" />
            </div>
            <h1 className="font-display text-3xl font-bold">Confirmation failed</h1>
            <p className="text-zinc-400 text-sm mt-3">{msg}</p>
            <p className="text-sm text-zinc-500 mt-8">
              <Link to="/login" className="text-[#ccff00] hover:underline">Back to log in</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
